import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import StreamSettings from "@/components/StreamSettings";
import DrawingCanvas from "@/components/DrawingCanvas";
import { 
  ArrowLeft, Play, Pause, SkipBack, SkipForward, 
  Clock, Maximize, Minimize, X, Settings, ChevronLeft, ChevronRight, Timer, TimerOff,
  Monitor, Radio, Pencil
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRANSITION_EFFECTS = [
  { key: '1', id: 'fade', name: 'Fade' },
  { key: '2', id: 'slide', name: 'Slide' },
  { key: '3', id: 'zoom', name: 'Zoom' },
  { key: '4', id: 'flip', name: 'Flip' },
  { key: '5', id: 'dissolve', name: 'Dissolve' },
  { key: '6', id: 'none', name: 'None' },
];

const Presentation = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [autoPlay, setAutoPlay] = useState(false);
  const [autoScrollSpeed, setAutoScrollSpeed] = useState(5);
  const [progress, setProgress] = useState(0);
  
  // Transition override state
  const [liveTransition, setLiveTransition] = useState(null);
  const [showTransitionHint, setShowTransitionHint] = useState(false);
  
  // Streaming state
  const [showStreamSettings, setShowStreamSettings] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  
  // Drawing mode state
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  
  // Countdown timer state
  const [countdownActive, setCountdownActive] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(300); // Default 5 minutes
  const [countdownRemaining, setCountdownRemaining] = useState(300);
  const [showCountdownSettings, setShowCountdownSettings] = useState(false);
  const [countdownPosition, setCountdownPosition] = useState('corner'); // 'corner' or 'fullscreen'
  
  const containerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const autoPlayIntervalRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const broadcastChannel = useRef(null);
  const countdownIntervalRef = useRef(null);
  const transitionHintTimeoutRef = useRef(null);

  const fetchProject = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`);
      setProject(response.data);
      setAutoScrollSpeed(response.data.auto_scroll_speed || 5);
    } catch (error) {
      toast.error("Failed to load presentation");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  // BroadcastChannel for syncing with presenter view
  useEffect(() => {
    broadcastChannel.current = new BroadcastChannel(`slidecon-${projectId}`);
    
    broadcastChannel.current.onmessage = (event) => {
      if (event.data.type === 'SLIDE_CHANGE') {
        setCurrentIndex(event.data.index);
      }
    };

    return () => {
      broadcastChannel.current?.close();
    };
  }, [projectId]);

  // Broadcast slide changes to presenter view
  const broadcastSlideChange = (index) => {
    broadcastChannel.current?.postMessage({ type: 'SLIDE_CHANGE', index });
  };

  const openPresenterView = () => {
    window.open(`/presenter/${projectId}`, 'presenter-view', 'width=1400,height=900');
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Handle number keys 1-6 for transition effects
      if (['1', '2', '3', '4', '5', '6'].includes(e.key)) {
        const effect = TRANSITION_EFFECTS.find(t => t.key === e.key);
        if (effect) {
          setLiveTransition(effect.id);
          setShowTransitionHint(true);
          
          // Clear previous timeout
          if (transitionHintTimeoutRef.current) {
            clearTimeout(transitionHintTimeoutRef.current);
          }
          
          // Hide hint after 2 seconds
          transitionHintTimeoutRef.current = setTimeout(() => {
            setShowTransitionHint(false);
          }, 2000);
        }
        return;
      }
      
      switch (e.key) {
        case "ArrowRight":
        case " ":
        case "Enter":
          e.preventDefault();
          nextSlide();
          break;
        case "ArrowLeft":
          e.preventDefault();
          prevSlide();
          break;
        case "Escape":
          if (isFullscreen) {
            exitFullscreen();
          } else {
            navigate(`/editor/${projectId}`);
          }
          break;
        case "f":
        case "F":
          toggleFullscreen();
          break;
        case "p":
        case "P":
          setAutoPlay(prev => !prev);
          break;
        case "t":
        case "T":
          setShowCountdownSettings(prev => !prev);
          break;
        case "d":
        case "D":
          setIsDrawingMode(prev => !prev);
          break;
        case "0":
          // Reset to project default transition
          setLiveTransition(null);
          setShowTransitionHint(true);
          if (transitionHintTimeoutRef.current) {
            clearTimeout(transitionHintTimeoutRef.current);
          }
          transitionHintTimeoutRef.current = setTimeout(() => {
            setShowTransitionHint(false);
          }, 2000);
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      if (transitionHintTimeoutRef.current) {
        clearTimeout(transitionHintTimeoutRef.current);
      }
    };
  }, [isFullscreen, currentIndex, project?.slides?.length]);

  // Auto-play functionality
  useEffect(() => {
    if (autoPlay && project?.slides?.length > 1) {
      // Reset progress
      setProgress(0);
      
      // Start progress animation
      progressIntervalRef.current = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) return 0;
          return prev + (100 / (autoScrollSpeed * 10));
        });
      }, 100);

      // Change slide
      autoPlayIntervalRef.current = setInterval(() => {
        setCurrentIndex(prev => {
          if (prev >= project.slides.length - 1) {
            return 0; // Loop back to start
          }
          return prev + 1;
        });
        setProgress(0);
      }, autoScrollSpeed * 1000);
    }

    return () => {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current);
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [autoPlay, autoScrollSpeed, project?.slides?.length]);

  // Hide controls after inactivity
  useEffect(() => {
    const handleMouseMove = () => {
      setShowControls(true);
      
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      
      controlsTimeoutRef.current = setTimeout(() => {
        if (isFullscreen) {
          setShowControls(false);
        }
      }, 3000);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [isFullscreen]);

  const nextSlide = useCallback(() => {
    if (project?.slides && currentIndex < project.slides.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setProgress(0);
    }
  }, [currentIndex, project?.slides]);

  const prevSlide = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      setProgress(0);
    }
  }, [currentIndex]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const exitFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    setIsFullscreen(false);
  };

  // Listen for fullscreen change
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Countdown timer logic
  useEffect(() => {
    if (countdownActive && countdownRemaining > 0) {
      countdownIntervalRef.current = setInterval(() => {
        setCountdownRemaining(prev => {
          if (prev <= 1) {
            setCountdownActive(false);
            toast.success("Countdown complete!");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [countdownActive]);

  const startCountdown = (seconds) => {
    setCountdownSeconds(seconds);
    setCountdownRemaining(seconds);
    setCountdownActive(true);
    setShowCountdownSettings(false);
  };

  const stopCountdown = () => {
    setCountdownActive(false);
    setCountdownRemaining(countdownSeconds);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-white animate-pulse">Loading presentation...</div>
      </div>
    );
  }

  if (!project?.slides?.length) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white">
        <p className="mb-4">No slides in this presentation</p>
        <Button onClick={() => navigate(`/editor/${projectId}`)} variant="outline">
          Go to Editor
        </Button>
      </div>
    );
  }

  const currentSlide = project.slides[currentIndex];

  // Get transition configuration - liveTransition overrides all
  const transitionEffect = liveTransition || currentSlide?.transition || project?.transition_effect || "fade";
  const transitionDuration = project?.transition_duration || 0.3;

  // Define transition variants based on effect type
  const getTransitionVariants = () => {
    switch (transitionEffect) {
      case "slide":
        return {
          initial: { x: "100%", opacity: 0 },
          animate: { x: 0, opacity: 1 },
          exit: { x: "-100%", opacity: 0 }
        };
      case "zoom":
        return {
          initial: { scale: 0.8, opacity: 0 },
          animate: { scale: 1, opacity: 1 },
          exit: { scale: 1.2, opacity: 0 }
        };
      case "flip":
        return {
          initial: { rotateY: 90, opacity: 0 },
          animate: { rotateY: 0, opacity: 1 },
          exit: { rotateY: -90, opacity: 0 }
        };
      case "dissolve":
        return {
          initial: { opacity: 0, filter: "blur(10px)" },
          animate: { opacity: 1, filter: "blur(0px)" },
          exit: { opacity: 0, filter: "blur(10px)" }
        };
      case "none":
        return {
          initial: {},
          animate: {},
          exit: {}
        };
      case "fade":
      default:
        return {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          exit: { opacity: 0 }
        };
    }
  };

  const transitionVariants = getTransitionVariants();

  // Helper function to get background style
  const getBackgroundStyle = (slide) => {
    if (slide.background_type === 'gradient' && slide.background_image) {
      return { background: slide.background_image };
    }
    return { backgroundColor: slide.background_color };
  };

  return (
    <div 
      ref={containerRef}
      className="min-h-screen bg-black presentation-mode relative overflow-hidden"
      style={{ perspective: transitionEffect === 'flip' ? '1000px' : 'none' }}
      data-testid="presentation-container"
    >
      {/* Slide Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          variants={transitionVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: transitionDuration, ease: "easeInOut" }}
          className="absolute inset-0 flex items-center justify-center"
          style={{ 
            ...getBackgroundStyle(currentSlide),
            color: currentSlide.text_color,
            transformStyle: transitionEffect === 'flip' ? 'preserve-3d' : 'flat'
          }}
          onClick={() => setShowControls(!showControls)}
          data-testid="current-slide"
        >
          {/* Background Video */}
          {currentSlide.background_type === 'video' && currentSlide.background_video && (
            <video 
              src={currentSlide.background_video}
              className="absolute inset-0 w-full h-full object-cover"
              autoPlay
              loop
              muted
              playsInline
            />
          )}
          {/* Background Image */}
          {currentSlide.background_type === 'image' && currentSlide.background_image && (
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${currentSlide.background_image})` }}
            />
          )}
          {/* Background GIF */}
          {currentSlide.background_type === 'gif' && currentSlide.background_image && (
            <img 
              src={currentSlide.background_image}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
          {/* Overlay for readability */}
          {(currentSlide.background_type === 'image' || currentSlide.background_type === 'gradient' || currentSlide.background_type === 'video' || currentSlide.background_type === 'gif') && (currentSlide.background_image || currentSlide.background_video) && (
            <div className="absolute inset-0 bg-black/30" />
          )}
          <div className="max-w-5xl px-8 text-center relative z-10 max-h-[80vh] overflow-hidden">
            {/* Type Badge */}
            {currentSlide.type !== 'text' && currentSlide.type !== 'title' && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="mb-8"
              >
                <span className={currentSlide.type === 'chorus' ? 'chorus-badge text-lg' : 'verse-badge text-lg'}>
                  {currentSlide.type.toUpperCase()}
                </span>
              </motion.div>
            )}
            
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="overflow-hidden"
            >
              <p 
                className="font-lyrics leading-relaxed whitespace-pre-line drop-shadow-lg overflow-hidden"
                style={{ 
                  fontSize: `clamp(20px, ${currentSlide.font_size / 12}vw, ${currentSlide.font_size * 1.2}px)`,
                  letterSpacing: '0.02em',
                  textShadow: '2px 2px 8px rgba(0,0,0,0.5)',
                  maxHeight: '70vh',
                  display: '-webkit-box',
                  WebkitLineClamp: 15,
                  WebkitBoxOrient: 'vertical'
                }}
              >
                {currentSlide.content}
              </p>
            </motion.div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Auto-scroll Progress Bar */}
      {autoPlay && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
          <motion.div 
            className="h-full bg-gradient-to-r from-amber-500 to-amber-600"
            style={{ width: `${progress}%` }}
            transition={{ duration: 0.1 }}
          />
        </div>
      )}

      {/* Countdown Timer Display */}
      {countdownActive && countdownPosition === 'corner' && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="absolute top-4 right-20 z-30"
        >
          <div className="bg-black/70 backdrop-blur-sm px-4 py-2 rounded-lg flex items-center gap-3 border border-amber-500/30">
            <Timer className="w-5 h-5 text-amber-500" />
            <span className={`font-mono text-2xl font-bold ${
              countdownRemaining <= 60 ? 'text-red-500 animate-pulse' : 'text-white'
            }`}>
              {formatTime(countdownRemaining)}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={stopCountdown}
              className="w-6 h-6 text-white/70 hover:text-red-500 hover:bg-transparent"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </motion.div>
      )}

      {/* Fullscreen Countdown (before service starts) */}
      {countdownActive && countdownPosition === 'fullscreen' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 z-40 bg-black/90 flex flex-col items-center justify-center"
        >
          <Timer className="w-16 h-16 text-amber-500 mb-6" />
          <span className={`font-mono text-8xl font-bold ${
            countdownRemaining <= 60 ? 'text-red-500 animate-pulse' : 'text-white'
          }`}>
            {formatTime(countdownRemaining)}
          </span>
          <p className="text-white/60 text-lg mt-4">Service starts soon</p>
          <Button
            variant="outline"
            onClick={stopCountdown}
            className="mt-8 gap-2"
          >
            <TimerOff className="w-4 h-4" />
            Stop Countdown
          </Button>
        </motion.div>
      )}

      {/* Countdown Settings Popup */}
      {showCountdownSettings && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute top-20 right-4 z-50 bg-slate-900 rounded-lg p-4 border border-slate-700 shadow-xl w-72"
        >
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <Timer className="w-4 h-4 text-amber-500" />
            Countdown Timer
          </h3>
          
          <div className="space-y-4">
            {/* Preset times */}
            <div className="grid grid-cols-3 gap-2">
              {[60, 180, 300, 600, 900, 1800].map((sec) => (
                <Button
                  key={sec}
                  variant="outline"
                  size="sm"
                  onClick={() => startCountdown(sec)}
                  className="text-xs"
                >
                  {sec < 60 ? `${sec}s` : `${sec / 60}m`}
                </Button>
              ))}
            </div>
            
            {/* Display mode */}
            <div className="flex items-center justify-between pt-2 border-t border-slate-700">
              <span className="text-white/70 text-sm">Display Mode</span>
              <div className="flex gap-1">
                <Button
                  variant={countdownPosition === 'corner' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCountdownPosition('corner')}
                  className="text-xs"
                >
                  Corner
                </Button>
                <Button
                  variant={countdownPosition === 'fullscreen' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCountdownPosition('fullscreen')}
                  className="text-xs"
                >
                  Full
                </Button>
              </div>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowCountdownSettings(false)}
            className="absolute top-2 right-2 w-6 h-6 text-white/50 hover:text-white"
          >
            <X className="w-4 h-4" />
          </Button>
        </motion.div>
      )}

      {/* Transition Effect Hint */}
      <AnimatePresence>
        {showTransitionHint && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50"
          >
            <div className="bg-black/80 backdrop-blur-sm px-6 py-3 rounded-lg border border-amber-500/50 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded bg-amber-500 flex items-center justify-center">
                  <span className="text-black font-bold">
                    {liveTransition ? TRANSITION_EFFECTS.find(t => t.id === liveTransition)?.key || '•' : '0'}
                  </span>
                </div>
                <div>
                  <p className="text-white font-medium capitalize">
                    {liveTransition 
                      ? TRANSITION_EFFECTS.find(t => t.id === liveTransition)?.name 
                      : 'Default'} Transition
                  </p>
                  <p className="text-white/60 text-xs">
                    {liveTransition ? 'Live override active' : 'Using project default'}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Keyboard Shortcuts Help (shown in controls) */}
      {showControls && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute bottom-20 left-4 z-30"
        >
          <div className="bg-black/60 backdrop-blur-sm rounded-lg px-3 py-2 text-xs text-white/60">
            <p className="font-medium text-white/80 mb-1">Transitions: 1-6</p>
            <div className="grid grid-cols-3 gap-x-4 gap-y-0.5">
              {TRANSITION_EFFECTS.map(t => (
                <span key={t.key} className={liveTransition === t.id ? 'text-amber-400' : ''}>
                  {t.key}: {t.name}
                </span>
              ))}
            </div>
            <p className="mt-1 text-white/50">0: Reset to default</p>
          </div>
        </motion.div>
      )}

      {/* Top Controls Bar */}
      <motion.div
        className={`absolute top-0 left-0 right-0 p-4 flex items-center justify-between transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)' }}
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/editor/${projectId}`)}
          className="text-white hover:bg-white/20"
          data-testid="exit-presentation-btn"
        >
          <X className="w-6 h-6" />
        </Button>

        <div className="flex items-center gap-4">
          <span className="text-white text-sm font-medium">
            {project.name}
          </span>
          {project.is_song && (
            <span className="px-2 py-1 bg-amber-500 text-black text-xs font-semibold rounded">
              SONG MODE
            </span>
          )}
          {isStreaming && (
            <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-semibold rounded flex items-center gap-1">
              <Radio className="w-3 h-3 animate-pulse" />
              LIVE
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Presenter View Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={openPresenterView}
            className="text-white hover:bg-white/20"
            data-testid="presenter-view-btn"
            title="Open Presenter View (Speaker Notes)"
          >
            <Monitor className="w-6 h-6" />
          </Button>

          {/* Stream Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowStreamSettings(true)}
            className={`text-white hover:bg-white/20 ${isStreaming ? 'text-red-400' : ''}`}
            data-testid="stream-btn"
            title="Live Stream Settings"
          >
            <Radio className="w-6 h-6" />
          </Button>

          {/* Drawing Mode Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsDrawingMode(!isDrawingMode)}
            className={`text-white hover:bg-white/20 ${isDrawingMode ? 'text-amber-500 bg-amber-500/20' : ''}`}
            data-testid="drawing-btn"
            title="Drawing Mode (D)"
          >
            <Pencil className="w-6 h-6" />
          </Button>

          {/* Countdown Timer Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowCountdownSettings(!showCountdownSettings)}
            className={`text-white hover:bg-white/20 ${countdownActive ? 'text-amber-500' : ''}`}
            data-testid="countdown-btn"
            title="Countdown Timer"
          >
            <Timer className="w-6 h-6" />
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleFullscreen}
            className="text-white hover:bg-white/20"
            data-testid="fullscreen-btn"
          >
            {isFullscreen ? <Minimize className="w-6 h-6" /> : <Maximize className="w-6 h-6" />}
          </Button>
        </div>
      </motion.div>

      {/* Drawing Canvas Overlay */}
      <DrawingCanvas
        isActive={isDrawingMode}
        onClose={() => setIsDrawingMode(false)}
        slideIndex={currentIndex}
      />

      {/* Bottom Controls */}
      <motion.div
        className={`absolute bottom-0 left-0 right-0 p-6 transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)' }}
      >
        <div className="max-w-4xl mx-auto">
          {/* Main Controls */}
          <div className="flex items-center justify-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={prevSlide}
              disabled={currentIndex === 0}
              className="text-white hover:bg-white/20 disabled:opacity-30"
              data-testid="prev-btn"
            >
              <SkipBack className="w-6 h-6" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCurrentIndex(0)}
              className="text-white hover:bg-white/20"
              data-testid="restart-btn"
            >
              <ChevronLeft className="w-6 h-6" />
              <ChevronLeft className="w-6 h-6 -ml-4" />
            </Button>

            <Button
              size="lg"
              onClick={() => setAutoPlay(!autoPlay)}
              className={`rounded-full w-16 h-16 ${
                autoPlay 
                  ? 'bg-amber-500 hover:bg-amber-600 text-black' 
                  : 'bg-white/20 hover:bg-white/30 text-white'
              }`}
              data-testid="play-pause-btn"
            >
              {autoPlay ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCurrentIndex(project.slides.length - 1)}
              className="text-white hover:bg-white/20"
              data-testid="end-btn"
            >
              <ChevronRight className="w-6 h-6" />
              <ChevronRight className="w-6 h-6 -ml-4" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={nextSlide}
              disabled={currentIndex === project.slides.length - 1}
              className="text-white hover:bg-white/20 disabled:opacity-30"
              data-testid="next-btn"
            >
              <SkipForward className="w-6 h-6" />
            </Button>
          </div>

          {/* Slide Counter & Speed Control */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-white/70 text-sm">
                Slide {currentIndex + 1} of {project.slides.length}
              </span>
            </div>

            <div className="flex items-center gap-6">
              {/* Auto-scroll speed */}
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-white/70" />
                <Slider
                  value={[autoScrollSpeed]}
                  min={2}
                  max={15}
                  step={1}
                  onValueChange={([value]) => setAutoScrollSpeed(value)}
                  className="w-24"
                  data-testid="speed-slider"
                />
                <span className="text-white/70 text-sm w-8">{autoScrollSpeed}s</span>
              </div>

              {/* Auto-play toggle */}
              <div className="flex items-center gap-2">
                <span className="text-white/70 text-sm">Auto</span>
                <Switch
                  checked={autoPlay}
                  onCheckedChange={setAutoPlay}
                  data-testid="auto-play-switch"
                />
              </div>
            </div>
          </div>

          {/* Keyboard Shortcuts */}
          <div className="mt-4 flex items-center justify-center gap-6 text-white/40 text-xs flex-wrap">
            <span><kbd className="kbd">←</kbd> / <kbd className="kbd">→</kbd> Navigate</span>
            <span><kbd className="kbd">Space</kbd> Next</span>
            <span><kbd className="kbd">F</kbd> Fullscreen</span>
            <span><kbd className="kbd">P</kbd> Play/Pause</span>
            <span><kbd className="kbd">D</kbd> Draw</span>
            <span><kbd className="kbd">T</kbd> Timer</span>
            <span><kbd className="kbd">Esc</kbd> Exit</span>
          </div>
        </div>
      </motion.div>

      {/* Slide Thumbnails (when not in fullscreen) */}
      {!isFullscreen && showControls && project.slides.length <= 10 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute bottom-36 left-1/2 transform -translate-x-1/2 flex gap-2 p-2 bg-black/60 backdrop-blur-sm rounded-lg z-10"
        >
          {project.slides.map((slide, index) => (
            <button
              key={slide.id}
              onClick={() => {
                setCurrentIndex(index);
                setProgress(0);
              }}
              className={`w-16 h-9 rounded overflow-hidden transition-all ${
                index === currentIndex 
                  ? 'ring-2 ring-amber-500 scale-110' 
                  : 'opacity-60 hover:opacity-100'
              }`}
              style={{ backgroundColor: slide.background_color }}
              data-testid={`thumbnail-${index}`}
            >
              <div 
                className="w-full h-full flex items-center justify-center text-[6px] p-1 line-clamp-2"
                style={{ color: slide.text_color }}
              >
                {slide.content.substring(0, 30)}
              </div>
            </button>
          ))}
        </motion.div>
      )}

      {/* Stream Settings Dialog */}
      <StreamSettings
        open={showStreamSettings}
        onClose={() => setShowStreamSettings(false)}
        projectId={projectId}
        isStreaming={isStreaming}
        onStreamStart={(config) => {
          setIsStreaming(true);
          broadcastChannel.current?.postMessage({ type: 'STREAM_STATUS', streaming: true });
        }}
        onStreamStop={() => {
          setIsStreaming(false);
          broadcastChannel.current?.postMessage({ type: 'STREAM_STATUS', streaming: false });
        }}
      />

      {/* Custom Branding Overlay (Pro+ feature) */}
      {project.branding_enabled && (
        <>
          {/* Logo */}
          {project.branding_logo_url && (
            <div 
              className={`absolute z-20 pointer-events-none ${
                project.branding_logo_position === 'top-left' ? 'top-4 left-4' :
                project.branding_logo_position === 'top-right' ? 'top-4 right-4' :
                project.branding_logo_position === 'bottom-left' ? 'bottom-24 left-4' :
                'bottom-24 right-4'
              }`}
              style={{ opacity: project.branding_logo_opacity || 0.8 }}
              data-testid="branding-logo"
            >
              <img 
                src={project.branding_logo_url} 
                alt="Branding" 
                className={`object-contain ${
                  project.branding_logo_size === 'small' ? 'h-8 max-w-[120px]' :
                  project.branding_logo_size === 'medium' ? 'h-12 max-w-[180px]' :
                  'h-16 max-w-[240px]'
                }`}
              />
            </div>
          )}
          
          {/* Branding Text */}
          {project.branding_text && (
            <div 
              className={`absolute z-20 pointer-events-none ${
                project.branding_text_position === 'bottom-left' ? 'bottom-24 left-4' :
                project.branding_text_position === 'bottom-right' ? 'bottom-24 right-4' :
                'bottom-24 left-1/2 transform -translate-x-1/2'
              }`}
              style={{ opacity: project.branding_logo_opacity || 0.8 }}
              data-testid="branding-text"
            >
              <span className="text-white/80 text-sm font-medium px-3 py-1 bg-black/30 rounded-full backdrop-blur-sm">
                {project.branding_text}
              </span>
            </div>
          )}
        </>
      )}

      {/* Watermark for Free Tier (shown when custom branding is disabled) */}
      {!project.branding_enabled && (
        <div 
          className="absolute bottom-24 right-4 z-20 pointer-events-none opacity-40"
          data-testid="slidecon-watermark"
        >
          <span className="text-white/60 text-xs font-medium px-2 py-1 bg-black/20 rounded">
            Made with SlideCon
          </span>
        </div>
      )}
    </div>
  );
};

export default Presentation;
